import type React from "react"
import { Col, Row } from "reactstrap"
import type { DashboardStats } from "./types/dashboard"
import "../../CommonElements/DashboardTheme/dashboard-theme.css"

interface StatsCardsProps {
  stats: DashboardStats | null
  loading: boolean
}

interface KpiDefinition {
  label: string
  value: string
  sub?: string
  icon: string
  accent: "indigo" | "emerald" | "cyan" | "amber" | "rose" | "slate"
}

const StatsCards: React.FC<StatsCardsProps> = ({ stats, loading }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "FRW",
    }).format(amount)
  }

  const calculateUsagePercent = () => {
    if (!stats?.ticketStats.totalTickets) return 0
    return Math.round((stats.ticketStats.usedTickets / stats.ticketStats.totalTickets) * 100)
  }

  if (loading) {
    return (
      <Row>
        {Array.from({ length: 8 }).map((_, index) => (
          <Col xl="3" lg="4" md="6" sm="6" key={index} className="mb-3">
            <div className="db-kpi-card">
              <div className="db-kpi-top">
                <div>
                  <div className="db-skeleton mb-2" style={{ height: "12px", width: "90px" }}></div>
                  <div className="db-skeleton" style={{ height: "26px", width: "70px" }}></div>
                </div>
                <div className="db-skeleton" style={{ width: "42px", height: "42px", borderRadius: "8px" }}></div>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    )
  }

  const usagePercent = calculateUsagePercent()

  const kpis: KpiDefinition[] = [
    {
      label: "Total Exhibitors",
      value: `${stats?.totalExhibitors || 0}`,
      icon: "fa-users",
      accent: "indigo",
    },
    {
      label: "Total Badges",
      value: `${stats?.totalBadges.total || 0}`,
      sub: `${stats?.totalBadges.free || 0} Normal · ${stats?.totalBadges.extra || 0} Extra`,
      icon: "fa-id-badge",
      accent: "emerald",
    },
    {
      label: "Normal Badges",
      value: `${stats?.totalBadges.free || 0}`,
      icon: "fa-gift",
      accent: "cyan",
    },
    {
      label: "Extra Badges",
      value: `${stats?.totalBadges.extra || 0}`,
      sub: formatCurrency(stats?.extraBadgeAmount?.totalAmount || 0),
      icon: "fa-plus-circle",
      accent: "amber",
    },
    {
      label: "Vehicle Entries Revenue",
      value: formatCurrency(stats?.vehicleStats.totalAmount || 0),
      sub: `${stats?.vehicleStats.totalVisits || 0} Visits`,
      icon: "fa-truck",
      accent: "slate",
    },
    {
      label: "Ticket Sales Revenue",
      value: formatCurrency(stats?.ticketStats.totalAmount || 0),
      sub: `${stats?.ticketStats.totalTickets || 0} Total`,
      icon: "fa-dollar",
      accent: "cyan",
    },
    {
      label: "Used Tickets",
      value: `${stats?.ticketStats.usedTickets || 0}`,
      sub: `${usagePercent}% Usage Rate`,
      icon: "fa-check-circle",
      accent: "emerald",
    },
    {
      label: "Unused Tickets",
      value: `${stats?.ticketStats.unusedTickets || 0}`,
      sub: `${100 - usagePercent}% Remaining`,
      icon: "fa-times-circle",
      accent: "rose",
    },
  ]

  return (
    <Row>
      {kpis.map((kpi) => (
        <Col xl="3" lg="4" md="6" sm="6" key={kpi.label} className="mb-3">
          <div className="db-kpi-card">
            <div className="db-kpi-top">
              <div className="flex-grow-1 me-2">
                <div className="db-kpi-eyebrow">{kpi.label}</div>
                <div className="db-kpi-value">{kpi.value}</div>
                {kpi.sub && <div className="db-kpi-sub">{kpi.sub}</div>}
              </div>
              <div className={`db-kpi-chip db-accent-${kpi.accent}`}>
                <i className={`fa ${kpi.icon}`}></i>
              </div>
            </div>
          </div>
        </Col>
      ))}
    </Row>
  )
}

export default StatsCards
